(() => {
  if (window.__quarkRichTextLoaded) {
    return;
  }
  window.__quarkRichTextLoaded = true;

  const ROOT_SELECTOR = '[data-component="quark-rich-text"]';
  const LINE_TARGET_SELECTOR = '[data-qrt-line-target]';
  const ANIM_ITEM_SELECTOR = '[data-qrt-anim-item]';
  const LINE_TOLERANCE = 2;
  const COMPLETE_RATIO = 0.75;

  const instances = new Map();

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function easeOutQuad(t) {
    return 1 - (1 - t) * (1 - t);
  }

  function isSkippableElement(element) {
    if (!element || element.nodeType !== Node.ELEMENT_NODE) {
      return true;
    }

    const tag = element.tagName;
    return tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'TEXTAREA';
  }

  function isHighlightElement(element) {
    return Boolean(element && element.matches && element.matches('.q-highlight, mark'));
  }

  class QuarkRichText {
    constructor(root) {
      this.root = root;
      this.entrancePreset = root.dataset.entrance || 'none';
      this.motionDriver = root.dataset.highlightMotion || 'hybrid';
      this.once = root.dataset.entranceOnce !== 'false';
      this.scrollInfluence = clamp(Number(root.dataset.highlightScrollInfluence || 0), 0, 5);

      this.animItems = Array.from(root.querySelectorAll(ANIM_ITEM_SELECTOR));
      this.lineTargets = Array.from(root.querySelectorAll(LINE_TARGET_SELECTOR));
      this.nonLineItems = this.animItems.filter((item) => !item.matches(LINE_TARGET_SELECTOR));

      this.lineGroups = [];
      this.reducedMotion = prefersReducedMotion();
      this.entranceLocked = false;
      this.scrollListenerAttached = false;
      this.resizeListenerAttached = false;

      this.scrollRaf = null;
      this.resizeRaf = null;

      this.boundOnScroll = this.onScroll.bind(this);
      this.boundOnResize = this.onResize.bind(this);
      this.boundOnMotionChanged = this.onMotionPreferenceChanged.bind(this);

      this.motionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.init();
    }

    init() {
      if (this.motionMedia.addEventListener) {
        this.motionMedia.addEventListener('change', this.boundOnMotionChanged);
      } else if (this.motionMedia.addListener) {
        this.motionMedia.addListener(this.boundOnMotionChanged);
      }

      if (!this.reducedMotion && this.entrancePreset === 'line_reveal_up') {
        this.prepareLineTargets();
      }

      if (this.reducedMotion || this.entrancePreset === 'none') {
        this.root.classList.remove('is-animated');
        this.setAllEntranceProgress(1);
      } else {
        this.root.classList.add('is-animated');
      }

      this.setupListeners();
      this.scheduleFrame();
    }

    setupListeners() {
      this.highlightScrollEnabled = !this.reducedMotion && (this.motionDriver === 'hybrid' || this.motionDriver === 'scroll');
      this.entranceScrollEnabled = !this.reducedMotion && this.entrancePreset !== 'none' && !this.entranceLocked;

      const needsScroll = this.highlightScrollEnabled || this.entranceScrollEnabled;
      const needsResize = this.highlightScrollEnabled || this.entranceScrollEnabled || (!this.reducedMotion && this.entrancePreset === 'line_reveal_up');

      if (needsScroll && !this.scrollListenerAttached) {
        window.addEventListener('scroll', this.boundOnScroll, { passive: true });
        this.scrollListenerAttached = true;
      } else if (!needsScroll && this.scrollListenerAttached) {
        window.removeEventListener('scroll', this.boundOnScroll);
        this.scrollListenerAttached = false;
      }

      if (needsResize && !this.resizeListenerAttached) {
        window.addEventListener('resize', this.boundOnResize, { passive: true });
        this.resizeListenerAttached = true;
      } else if (!needsResize && this.resizeListenerAttached) {
        window.removeEventListener('resize', this.boundOnResize);
        this.resizeListenerAttached = false;
      }
    }

    onScroll() {
      this.scheduleFrame();
    }

    onResize() {
      if (this.resizeRaf !== null) {
        return;
      }

      this.resizeRaf = window.requestAnimationFrame(() => {
        this.resizeRaf = null;

        if (!this.reducedMotion && this.entrancePreset === 'line_reveal_up' && !this.entranceLocked) {
          this.prepareLineTargets();
        }

        this.scheduleFrame();
      });
    }

    scheduleFrame() {
      if (this.scrollRaf !== null) {
        return;
      }

      this.scrollRaf = window.requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.updateFrame();
      });
    }

    updateFrame() {
      if (this.highlightScrollEnabled) {
        this.updateHighlightProgress();
      }

      if (this.entranceScrollEnabled) {
        this.updateEntranceProgress();
      }
    }

    updateHighlightProgress() {
      const rect = this.root.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      if (!viewportHeight) {
        return;
      }

      const sectionProgress = (viewportHeight - rect.top) / (viewportHeight + rect.height);
      const normalized = clamp(sectionProgress, 0, 1);
      const centered = (normalized - 0.5) * 2;

      const shiftX = centered * 18 * this.scrollInfluence;
      const shiftY = centered * -12 * this.scrollInfluence;

      this.root.style.setProperty('--qrt-scroll-progress', centered.toFixed(4));
      this.root.style.setProperty('--qrt-scroll-shift-x', `${shiftX.toFixed(3)}%`);
      this.root.style.setProperty('--qrt-scroll-shift-y', `${shiftY.toFixed(3)}%`);
    }

    updateEntranceProgress() {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      if (!viewportHeight) {
        return;
      }

      const startY = viewportHeight;
      const endY = viewportHeight * COMPLETE_RATIO;

      if (this.entrancePreset === 'fade_up_stagger') {
        this.animItems.forEach((item, index) => {
          const top = item.getBoundingClientRect().top + index * 12;
          const progress = easeOutQuad(this.progressBetween(top, startY, endY));
          item.style.setProperty('--qrt-item-progress', progress.toFixed(4));
        });

        const lastItem = this.animItems[this.animItems.length - 1];
        const isComplete = !lastItem || lastItem.getBoundingClientRect().top <= endY;

        if (this.once && isComplete) {
          this.lockEntrance();
        }

        return;
      }

      if (this.entrancePreset === 'line_reveal_up') {
        this.lineGroups.forEach((group) => {
          const anchorWord = group.words[0];
          if (!anchorWord) {
            return;
          }

          const top = anchorWord.getBoundingClientRect().top;
          const progress = easeOutQuad(this.progressBetween(top, startY, endY));

          group.words.forEach((word) => {
            word.style.setProperty('--qrt-line-progress', progress.toFixed(4));
          });
        });

        this.nonLineItems.forEach((item, index) => {
          const top = item.getBoundingClientRect().top + index * 10;
          const progress = easeOutQuad(this.progressBetween(top, startY, endY));
          item.style.setProperty('--qrt-item-progress', progress.toFixed(4));
        });

        const lastGroup = this.lineGroups[this.lineGroups.length - 1];
        const lastGroupTop = lastGroup && lastGroup.words[0] ? lastGroup.words[0].getBoundingClientRect().top : null;
        const isComplete = lastGroupTop === null ? true : lastGroupTop <= endY;

        if (this.once && isComplete) {
          this.lockEntrance();
        }
      }
    }

    progressBetween(top, startY, endY) {
      const range = startY - endY;
      if (range <= 0) {
        return top <= endY ? 1 : 0;
      }

      return clamp((startY - top) / range, 0, 1);
    }

    lockEntrance() {
      this.entranceLocked = true;
      this.setAllEntranceProgress(1);
      this.setupListeners();
    }

    setAllEntranceProgress(value) {
      const progress = String(clamp(value, 0, 1));

      this.animItems.forEach((item) => {
        item.style.setProperty('--qrt-item-progress', progress);
      });

      this.lineTargets.forEach((target) => {
        target.querySelectorAll('.qrt-word').forEach((word) => {
          word.style.setProperty('--qrt-line-progress', progress);
        });
      });
    }

    prepareLineTargets() {
      this.lineGroups = [];

      this.lineTargets.forEach((target) => {
        this.restoreTargetMarkup(target);
        this.wrapWords(target);
        const groups = this.assignLineIndexes(target);

        groups.forEach((group) => {
          this.lineGroups.push(group);
        });
      });
    }

    restoreTargetMarkup(target) {
      if (!target.dataset.qrtOriginalHtml) {
        target.dataset.qrtOriginalHtml = target.innerHTML;
      }

      target.innerHTML = target.dataset.qrtOriginalHtml;
    }

    wrapWords(node) {
      const childNodes = Array.from(node.childNodes);

      childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const text = child.textContent;

          if (!text || !text.trim()) {
            return;
          }

          const tokens = text.split(/(\s+)/);
          const fragment = document.createDocumentFragment();

          tokens.forEach((token) => {
            if (!token) {
              return;
            }

            if (/^\s+$/.test(token)) {
              fragment.appendChild(document.createTextNode(token));
              return;
            }

            const word = document.createElement('span');
            word.className = 'qrt-word';
            word.textContent = token;
            fragment.appendChild(word);
          });

          child.replaceWith(fragment);
          return;
        }

        if (child.nodeType !== Node.ELEMENT_NODE) {
          return;
        }

        if (isHighlightElement(child)) {
          child.classList.add('qrt-word');
          return;
        }

        if (isSkippableElement(child) || child.classList.contains('qrt-word')) {
          return;
        }

        this.wrapWords(child);
      });
    }

    assignLineIndexes(target) {
      const words = Array.from(target.querySelectorAll('.qrt-word'));
      if (!words.length) {
        return [];
      }

      let lineIndex = -1;
      let previousTop = null;
      const groupsByIndex = new Map();

      words.forEach((word) => {
        const top = Math.round(word.getBoundingClientRect().top);

        if (previousTop === null || Math.abs(top - previousTop) > LINE_TOLERANCE) {
          lineIndex += 1;
          previousTop = top;
        }

        if (!groupsByIndex.has(lineIndex)) {
          groupsByIndex.set(lineIndex, []);
        }

        groupsByIndex.get(lineIndex).push(word);
        word.style.setProperty('--qrt-line-index', String(lineIndex));
      });

      return Array.from(groupsByIndex.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, groupedWords]) => ({ words: groupedWords }));
    }

    onMotionPreferenceChanged() {
      this.reducedMotion = prefersReducedMotion();

      if (this.reducedMotion) {
        this.root.classList.remove('is-animated');
        this.setAllEntranceProgress(1);
      } else {
        this.entranceLocked = false;

        if (this.entrancePreset !== 'none') {
          this.root.classList.add('is-animated');

          if (this.entrancePreset === 'line_reveal_up') {
            this.prepareLineTargets();
          }
        }
      }

      this.setupListeners();
      this.scheduleFrame();
    }

    destroy() {
      if (this.motionMedia.removeEventListener) {
        this.motionMedia.removeEventListener('change', this.boundOnMotionChanged);
      } else if (this.motionMedia.removeListener) {
        this.motionMedia.removeListener(this.boundOnMotionChanged);
      }

      if (this.scrollListenerAttached) {
        window.removeEventListener('scroll', this.boundOnScroll);
      }

      if (this.resizeListenerAttached) {
        window.removeEventListener('resize', this.boundOnResize);
      }

      if (this.scrollRaf !== null) {
        window.cancelAnimationFrame(this.scrollRaf);
      }

      if (this.resizeRaf !== null) {
        window.cancelAnimationFrame(this.resizeRaf);
      }

      this.lineTargets.forEach((target) => {
        if (target.dataset.qrtOriginalHtml) {
          target.innerHTML = target.dataset.qrtOriginalHtml;
        }
      });
    }
  }

  function initWithin(scope) {
    const roots = Array.from(scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      if (instances.has(root)) {
        return;
      }

      instances.set(root, new QuarkRichText(root));
    });
  }

  function destroyWithin(scope) {
    const roots = Array.from(scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      const instance = instances.get(root);
      if (!instance) {
        return;
      }

      instance.destroy();
      instances.delete(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document));
  } else {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) {
      initWithin(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target) {
      destroyWithin(event.target);
    }
  });
})();
